import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Search, Eye } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  confirmed:  "bg-green-100 text-green-800",
  pending:    "bg-yellow-100 text-yellow-800",
  cancelled:  "bg-red-100 text-red-700",
  completed:  "bg-blue-100 text-blue-800",
};

const STATUS_FILTERS = ["All", "confirmed", "pending", "completed", "cancelled"];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function UmrahBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah-bookings`, { headers: getAuthHeaders() });
      setBookings(r.data || []);
    } catch { /**/ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = bookings.filter(b => {
    if (statusFilter !== "All" && b.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        (b.customer_name || "").toLowerCase().includes(s) ||
        (b.customer_email || "").toLowerCase().includes(s) ||
        (b.booking_reference || "").toLowerCase().includes(s) ||
        (b.quotation_number || "").toLowerCase().includes(s) ||
        (b.package_reference || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Moon className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Umrah Bookings</h1>
            <p className="text-sm text-gray-500">{bookings.length} booking{bookings.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
            {STATUS_FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition-colors ${
                  statusFilter === f ? "bg-white shadow text-amber-800" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customer, booking #, quotation #…"
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              {bookings.length === 0 ? "No bookings yet." : "No bookings match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Booking #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Quotation</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Travel Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Travellers</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pilgrims</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(b => (
                    <tr key={b.id} className="hover:bg-amber-50 transition-colors cursor-pointer" onClick={() => navigate(`/umrah-bookings/${b.id}`)}>
                      <td className="px-4 py-3 font-mono text-xs text-amber-800 font-semibold whitespace-nowrap">
                        {b.booking_reference || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{b.customer_name || "—"}</div>
                        {b.customer_email && <div className="text-xs text-gray-400">{b.customer_email}</div>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">
                        {b.quotation_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {b.flexible_dates
                          ? <span className="italic text-gray-400">Flexible</span>
                          : (b.travel_start_date ? `${fmtDate(b.travel_start_date)} – ${fmtDate(b.travel_end_date)}` : "—")}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-center">{b.number_of_travellers || "—"}</td>
                      <td className="px-4 py-3 text-gray-700 text-center">{(b.pilgrims || []).length}</td>
                      <td className="px-4 py-3 font-semibold text-amber-800 whitespace-nowrap">
                        {b.agreed_currency || "€"}{(b.agreed_total_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[b.status] || "bg-gray-100 text-gray-600"}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <button
                          title="View booking"
                          onClick={() => navigate(`/umrah-bookings/${b.id}`)}
                          className="p-1.5 text-gray-400 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
